// THE BOARD'S STATE MACHINE (spec 09 §4.14 "Moving a deal"; plan §7's board
// reducer), pure, so every drag, drop, refusal and rollback is unit-tested
// without a browser (`__tests__/board-state.test.ts`). The screen dispatches;
// this file decides.
//
// WHAT IT HOLDS. The server's columns are never copied here. The state is
// what the reader is doing to them: a card pressed or dragged (`drag`), a
// drop on Lost waiting for its reason (`awaitingReason`), the one move on its
// way to the server (`pending`), and the one line the board shows after a
// refused or lost move (`alert`). `placeCards` lays the pending move over the
// server's columns, so a card moves at once and goes back by itself: when
// `pending` clears, the server's own columns are what the board shows.
//
// THE ROLLBACK IS TO COLUMN AND INDEX. A refusal clears `pending`, so the
// card reappears where the server still has it, which is where it was picked
// up: the column and the index the drag recorded (`origin`). The screen then
// reads the board again, so a card that someone else moved ends where the
// deal really is (the `OPPORTUNITY_STAGE_CHANGED` case).
//
// ONE MOVE AT A TIME. While a move is pending, pressing or picking up another
// card does nothing. The board's rows are one server truth, and two moves in
// flight would each lay themselves over a board the other is about to
// change.
//
// Rules pinned here:
//   * a pointer press becomes a drag only after it travels 6 px (a click on a
//     card's title or button stays a click); a touch never starts a drag, and
//     the screen does not dispatch `press` for one;
//   * a drop on the stage the card left sends nothing, because it would move
//     nothing (the server answers such a move 200 and writes nothing);
//   * a drop on Lost sends nothing either: it opens the reason dialog, and
//     only the dialog's confirm makes it a move;
//   * Escape, `pointercancel` and a drop outside any target cancel;
//   * a network failure keeps the move, idempotency key and all, so Try again
//     sends the identical request (spec 09 §4.14; the key is minted once per
//     drop and travels in the action, because a reducer mints nothing).

import { boardOrder, type BoardOrdered } from "./pipeline";

/** How far a pointer travels before a press becomes a drag. */
export const DRAG_THRESHOLD_PX = 6;

export type DropTarget =
  | { readonly kind: "stage"; readonly stageId: string }
  | { readonly kind: "won" }
  | { readonly kind: "lost" };

/** Where a card was when it was picked up. */
export interface CardOrigin {
  readonly cardId: string;
  readonly stageId: string;
  readonly index: number;
}

export type DragState =
  | { readonly phase: "idle" }
  | {
      readonly phase: "pressed";
      readonly origin: CardOrigin;
      readonly pointerId: number;
      readonly x: number;
      readonly y: number;
    }
  | {
      readonly phase: "dragging";
      readonly via: "pointer" | "keyboard";
      readonly origin: CardOrigin;
      readonly over: DropTarget | null;
    };

/** A loss's reason and note, carried by a move to Lost. */
export interface LoseReason {
  readonly reason: "price" | "lead_time" | "stock" | "other";
  readonly note: string | undefined;
}

export interface PendingMove {
  readonly origin: CardOrigin;
  readonly target: DropTarget;
  /** Minted once per drop; a retry reuses it. */
  readonly idempotencyKey: string;
  /** Set only when the target is Lost. */
  readonly lose?: LoseReason | undefined;
}

export type BoardAlert =
  | {
      readonly kind: "refused";
      readonly move: PendingMove;
      /** The refused request's error, which the screen words. */
      readonly error: unknown;
    }
  | { readonly kind: "network"; readonly move: PendingMove };

export interface BoardState {
  readonly drag: DragState;
  /** A drop on Lost whose reason the dialog is asking for. */
  readonly awaitingReason: CardOrigin | null;
  readonly pending: PendingMove | null;
  readonly alert: BoardAlert | null;
}

export const INITIAL_BOARD_STATE: BoardState = {
  drag: { phase: "idle" },
  awaitingReason: null,
  pending: null,
  alert: null,
};

export type BoardAction =
  | {
      readonly type: "press";
      readonly origin: CardOrigin;
      readonly pointerId: number;
      readonly x: number;
      readonly y: number;
    }
  | {
      readonly type: "pointerMove";
      readonly pointerId: number;
      readonly x: number;
      readonly y: number;
    }
  | { readonly type: "hover"; readonly over: DropTarget | null }
  | { readonly type: "pickUp"; readonly origin: CardOrigin }
  | { readonly type: "step"; readonly targets: readonly DropTarget[]; readonly delta: 1 | -1 }
  | { readonly type: "drop"; readonly idempotencyKey: string }
  | { readonly type: "cancel" }
  | { readonly type: "askReason"; readonly origin: CardOrigin }
  | { readonly type: "confirmLost"; readonly idempotencyKey: string; readonly lose: LoseReason }
  | { readonly type: "moveDirect"; readonly move: PendingMove }
  | { readonly type: "settled" }
  | { readonly type: "refused"; readonly error: unknown }
  | { readonly type: "networkFailed" }
  | { readonly type: "retry" }
  | { readonly type: "dismissAlert" };

export function sameTarget(a: DropTarget | null, b: DropTarget | null): boolean {
  if (a === null || b === null) return a === b;
  if (a.kind !== b.kind) return false;
  return a.kind === "stage" && b.kind === "stage" ? a.stageId === b.stageId : true;
}

/** The target `delta` steps from `over` in the keyboard's order (the stages
 * in position order, then Won, then Lost), wrapping at both ends. From no
 * target, a step lands on the first or the last. */
export function stepTarget(
  targets: readonly DropTarget[],
  over: DropTarget | null,
  delta: 1 | -1,
): DropTarget | null {
  if (targets.length === 0) return null;
  const at = over === null ? -1 : targets.findIndex((target) => sameTarget(target, over));
  if (at < 0) return delta === 1 ? (targets[0] ?? null) : (targets[targets.length - 1] ?? null);
  return targets[(at + delta + targets.length) % targets.length] ?? null;
}

function busy(state: BoardState): boolean {
  return state.pending !== null || state.awaitingReason !== null;
}

export function boardReducer(state: BoardState, action: BoardAction): BoardState {
  switch (action.type) {
    case "press": {
      if (busy(state) || state.drag.phase !== "idle") return state;
      return {
        ...state,
        drag: {
          phase: "pressed",
          origin: action.origin,
          pointerId: action.pointerId,
          x: action.x,
          y: action.y,
        },
      };
    }
    case "pointerMove": {
      const drag = state.drag;
      if (drag.phase !== "pressed" || drag.pointerId !== action.pointerId) return state;
      const travelled = Math.hypot(action.x - drag.x, action.y - drag.y);
      if (travelled < DRAG_THRESHOLD_PX) return state;
      return {
        ...state,
        alert: null,
        drag: { phase: "dragging", via: "pointer", origin: drag.origin, over: null },
      };
    }
    case "hover": {
      const drag = state.drag;
      if (drag.phase !== "dragging" || sameTarget(drag.over, action.over)) return state;
      return { ...state, drag: { ...drag, over: action.over } };
    }
    case "pickUp": {
      if (busy(state) || state.drag.phase !== "idle") return state;
      return {
        ...state,
        alert: null,
        drag: {
          phase: "dragging",
          via: "keyboard",
          origin: action.origin,
          over: { kind: "stage", stageId: action.origin.stageId },
        },
      };
    }
    case "step": {
      const drag = state.drag;
      if (drag.phase !== "dragging") return state;
      return {
        ...state,
        drag: { ...drag, over: stepTarget(action.targets, drag.over, action.delta) },
      };
    }
    case "drop": {
      const drag = state.drag;
      if (drag.phase !== "dragging") return { ...state, drag: { phase: "idle" } };
      const over = drag.over;
      const idle: DragState = { phase: "idle" };
      if (over === null) return { ...state, drag: idle };
      if (over.kind === "stage" && over.stageId === drag.origin.stageId) {
        return { ...state, drag: idle };
      }
      if (over.kind === "lost") return { ...state, drag: idle, awaitingReason: drag.origin };
      return {
        ...state,
        drag: idle,
        alert: null,
        pending: { origin: drag.origin, target: over, idempotencyKey: action.idempotencyKey },
      };
    }
    case "cancel": {
      if (state.drag.phase === "idle" && state.awaitingReason === null) return state;
      return { ...state, drag: { phase: "idle" }, awaitingReason: null };
    }
    case "askReason": {
      // The Move dialog's Lost: the same reason dialog a drop on Lost opens.
      if (busy(state)) return state;
      return { ...state, drag: { phase: "idle" }, alert: null, awaitingReason: action.origin };
    }
    case "confirmLost": {
      if (state.awaitingReason === null || state.pending !== null) return state;
      return {
        ...state,
        awaitingReason: null,
        alert: null,
        pending: {
          origin: state.awaitingReason,
          target: { kind: "lost" },
          idempotencyKey: action.idempotencyKey,
          lose: action.lose,
        },
      };
    }
    case "moveDirect": {
      // The Move dialog: no drag, the same pending move and the same rollback.
      if (busy(state)) return state;
      return { ...state, drag: { phase: "idle" }, alert: null, pending: action.move };
    }
    case "settled":
      return state.pending === null ? state : { ...state, pending: null, alert: null };
    case "refused":
      return state.pending === null
        ? state
        : {
            ...state,
            pending: null,
            alert: { kind: "refused", move: state.pending, error: action.error },
          };
    case "networkFailed":
      return state.pending === null
        ? state
        : { ...state, pending: null, alert: { kind: "network", move: state.pending } };
    case "retry": {
      const alert = state.alert;
      if (alert?.kind !== "network" || busy(state)) return state;
      // A NEW object carrying the same move and the same key: the screen
      // sends each pending move once, by identity, so handing the old object
      // back would be a retry that never leaves.
      return { ...state, alert: null, pending: { ...alert.move } };
    }
    case "dismissAlert":
      return state.alert === null ? state : { ...state, alert: null };
  }
}

export interface PlacedColumn<C> {
  readonly stageId: string;
  readonly items: readonly C[];
}

/**
 * The server's columns with the pending move laid over them: the card leaves
 * the column it was picked from, and a move to a stage places it in that
 * column where the board's order puts it (a win or a loss takes it off the
 * board). Pure, and a no-op when nothing is pending or when the card is not
 * where the move says it was (a refetch already moved it).
 */
export function placeCards<C extends BoardOrdered>(
  columns: readonly PlacedColumn<C>[],
  pending: PendingMove | null,
): PlacedColumn<C>[] {
  if (pending === null) return columns.map((column) => ({ ...column }));
  let moving: C | undefined;
  for (const column of columns) {
    const found = column.items.find((item) => item.id === pending.origin.cardId);
    if (found !== undefined) {
      moving = found;
      break;
    }
  }
  if (moving === undefined) return columns.map((column) => ({ ...column }));
  const card = moving;
  const target = pending.target;
  return columns.map((column) => {
    const without = column.items.filter((item) => item.id !== card.id);
    if (target.kind === "stage" && target.stageId === column.stageId) {
      return { stageId: column.stageId, items: [...without, card].sort(boardOrder) };
    }
    return { stageId: column.stageId, items: without };
  });
}
